import { createElement, useCallback, useMemo, useState } from 'react';
import { useEditGiayBaoNoWithUpdatedInvoice, useMultipleForms, useSearchTable } from '../hooks';
import type { ChiTietGiayBaoNo, GiayBaoNo, GiayBaoNoInput } from '@/types/schemas';
import { getDataTableColumns, getInputTableColumns } from '../cols-definition';
import { initialSearchValues, SearchFormValues } from '../schema';
import { TABLE_PAGE_SIZE } from '../constants';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

const TABLE_TABS = [
  { name: 'Tất cả' },
  { name: 'Lập chứng từ', status: '0', color: '#EAD1DC' },
  { name: 'Chờ duyệt', status: '1', color: '#ef4444' },
  { name: 'Chi tiền', status: '5', color: '#3D85C6' },
  { name: 'Khác', status: 'other', color: '#000000' }
];

export const useGiayBaoNoTable = () => {
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const crud = useCRUD<GiayBaoNo, GiayBaoNoInput>({
    endpoint: QUERY_KEYS.GIAY_BAO_NO,
    pageSize: TABLE_PAGE_SIZE,
    sumField: 't_tien_nt',
    useFilter: true
  });
  const form = useForms<GiayBaoNo>(crud.fetchDetail);
  const multipleForms = useMultipleForms({ addGiayBaoNo: crud.addItem });
  const editWithUpdatedInvoice = useEditGiayBaoNoWithUpdatedInvoice();
  const searchTable = useSearchTable({
    searchData: crud.searchData,
    searchParams,
    setSearchParams,
    handleViewClick: form.handleViewClick,
    sumTotal: crud.sumTotal,
    isFiltered: crud.isFiltered,
    handlePageChange: crud.handlePageChange
  });

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      setSearchParams(filters);
      await searchTable.handleInitialSearch(filters);
    },
    [searchTable]
  );

  const handleRefresh = useCallback(async () => {
    if (searchTable.hasFilterParams) {
      await searchTable.handleRefreshClick();
    } else if (crud.isFiltered) {
      await crud.searchData(searchParams);
    } else {
      await crud.refreshData();
    }

    if (form.selectedObj?.uuid) {
      await crud.fetchDetail(form.selectedObj.uuid);
    }
  }, [crud, form.selectedObj?.uuid, searchParams, searchTable]);

  const displayData = useMemo(
    () => (searchTable.hasFilterParams ? searchTable.searchState.searchResults : crud.data || []),
    [crud.data, searchTable.hasFilterParams, searchTable.searchState.searchResults]
  );
  const totalAmount = searchTable.hasFilterParams
    ? (searchTable.searchState.totalRow?.t_tien_nt ?? crud.sumTotal)
    : crud.sumTotal;
  const columns = useMemo(
    () => getDataTableColumns(form.handleViewClick, totalAmount),
    [form.handleViewClick, totalAmount]
  );
  const tables = useMemo(
    () =>
      TABLE_TABS.map(tab => ({
        name: tab.name,
        rows:
          tab.status === 'other'
            ? displayData.filter(row => row.status !== '0' && row.status !== '1' && row.status !== '5')
            : tab.status
              ? displayData.filter(row => row.status === tab.status)
              : displayData,
        columns,
        icon: tab.color
          ? createElement('div', {
              className: 'mr-2 size-[7px] rounded-full',
              style: { backgroundColor: tab.color }
            })
          : undefined
      })),
    [columns, displayData]
  );

  return {
    crud,
    form,
    searchDialog: {
      open: form.showSearch,
      searchParams,
      onOpen: form.handleSearch,
      onClose: form.handleCloseSearch,
      onSubmit: handleSearchSubmit
    },
    multipleFormDialog: {
      open: multipleForms.showMultipleForms,
      initialData: multipleForms.initialData,
      invoiceData: multipleForms.invoiceData,
      onSubmit: multipleForms.handleMultipleFormSubmit,
      onClose: multipleForms.handleCloseMultipleForms
    },
    editWithUpdatedDialog: {
      open: editWithUpdatedInvoice.shouldEditWithUpdatedData,
      detail: editWithUpdatedInvoice.mergedFormData,
      uuid: editWithUpdatedInvoice.editGiayBaoNoUuid,
      isLoading: editWithUpdatedInvoice.isLoading
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading: crud.isLoading || crud.isSearching || searchTable.isFiltering,
      currentPage: searchTable.hasFilterParams ? searchTable.searchState.currentPage : crud.currentPage,
      totalItems: searchTable.hasFilterParams ? searchTable.searchState.totalCount : crud.totalItems,
      onPageChange: searchTable.onPageChange,
      onSearchChange: searchTable.handleSearchChange,
      onRefresh: handleRefresh
    },
    detailTable: {
      rows: form.selectedObj ? ((crud.detail as GiayBaoNo | null)?.chi_tiet_data ?? []) : [],
      columns: getInputTableColumns()
    } satisfies { rows: ChiTietGiayBaoNo[]; columns: ReturnType<typeof getInputTableColumns> },
    selection: {
      selectedObj: form.selectedObj,
      selectedRowIndex: form.selectedRowIndex,
      clearSelection: form.clearSelection
    }
  };
};
